import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import CloseIcon from '@mui/icons-material/Close'
import {
  Add,
  Article,
  Build,
  CheckCircle,
  EditNote,
  Forum,
  Leaderboard,
  Group,
  HourglassEmpty,
  ReplayCircleFilled,
  Visibility,
  WarningAmber,
} from '@mui/icons-material'
import { useAuth } from '../../../../../context/AuthContext'
import { useBlogsManagement } from '../../../hooks/useBlogsManagement'
import SearchBar from '../common/SearchBar'
import BlogsTable from '../tables/BlogsTable'
import BlogDialog from '../dialogs/BlogDialog'
import ConfirmDialog from '../dialogs/ConfirmDialog'
import { MetricCard } from '../styled/MetricCard'
import { formatTimestamp } from '../../../utils/dateUtils'
import apiService from '../../../../../services/ApiService'
import PreviewBlogModal from '../../../../PreviewBlogModal'

const themeColors = {
  primary: '#232536',
  secondary: '#ffcf51',
  hover: '#3a3e5a',
}

const BlogsTab = ({ showNotification, onCountChange }) => {
  const navigate = useNavigate()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'))
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const { user, isAuthenticated } = useAuth()
  const {
    blogs,
    reviewBlogs,
    metrics,
    loading,
    reviewLoading,
    metricsLoading,
    currentPage,
    reviewPage,
    totalPages,
    reviewTotalPages,
    totalItems,
    searchTerm,
    setSearchTerm,
    reviewSearchTerm,
    setReviewSearchTerm,
    reviewStatusFilter,
    loadBlogs,
    loadReviewBlogs,
    loadMetrics,
    createBlog,
    updateBlog,
    deleteBlog,
    reviewBlog,
    handleSearch,
    handleReviewSearch,
    handlePageChange,
    handleReviewPageChange,
    handleReviewStatusFilter,
  } = useBlogsManagement()

  const getInitialSubTab = () => {
    const params = new URLSearchParams(window.location.search)
    const subTab = (params.get('subTab') || '').toLowerCase()
    if (subTab === 'review') {
      return 1
    }
    if (subTab === 'metrics') {
      return 2
    }
    return 0
  }

  const [subTabValue, setSubTabValue] = useState(getInitialSubTab)
  const [openDialog, setOpenDialog] = useState(false)
  const [selectedBlog, setSelectedBlog] = useState(null)
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null,
  })
  const [reviewDialog, setReviewDialog] = useState({
    open: false,
    blogId: null,
    action: 'APPROVE',
    notes: '',
  })
  const [reviewDialogError, setReviewDialogError] = useState('')
  const [previewDialog, setPreviewDialog] = useState({
    open: false,
    loading: false,
    error: '',
    blog: null,
  })
  const deepLinkPreviewHandledRef = useRef(false)

  useEffect(() => {
    loadBlogs()
    loadReviewBlogs()
    loadMetrics()
  }, [loadBlogs, loadMetrics, loadReviewBlogs])

  useEffect(() => {
    const countValue = metrics?.totalBlogs ?? totalItems
    if (onCountChange && Number.isFinite(countValue)) {
      onCountChange(countValue)
    }
  }, [metrics?.totalBlogs, onCountChange, totalItems])

  const applySubTabChange = (newValue) => {
    setSubTabValue(newValue)
    const params = new URLSearchParams(window.location.search)
    params.set('tab', 'blogs')
    params.delete('subtab')
    if (newValue === 1) {
      params.set('subTab', 'review')
    } else if (newValue === 2) {
      params.set('subTab', 'metrics')
      params.delete('blogId')
    } else {
      params.set('subTab', 'team')
      params.delete('blogId')
    }
    window.history.replaceState({}, '', `${window.location.pathname}?${params}`)
  }

  const handleSubTabChange = (event, newValue) => {
    applySubTabChange(newValue)
  }

  const handleOpenPreview = async (blogId) => {
    setPreviewDialog({
      open: true,
      loading: true,
      error: '',
      blog: null,
    })
    try {
      const response = await apiService.getAdminBlogById(blogId, { bypassCache: true, skipCache: true })
      setPreviewDialog({
        open: true,
        loading: false,
        error: '',
        blog: response,
      })
    } catch (error) {
      const backendMessage = error?.response?.data?.error || error?.response?.data?.message
      const message = backendMessage || 'Failed to load blog preview'
      setPreviewDialog({
        open: true,
        loading: false,
        error: message,
        blog: null,
      })
    }
  }

  const handleClosePreview = () => {
    setPreviewDialog({
      open: false,
      loading: false,
      error: '',
      blog: null,
    })
  }

  useEffect(() => {
    if (subTabValue !== 1 || deepLinkPreviewHandledRef.current) {
      return
    }

    const params = new URLSearchParams(window.location.search)
    const deepLinkBlogId = params.get('blogId')
    if (!deepLinkBlogId) {
      return
    }

    deepLinkPreviewHandledRef.current = true
    handleOpenPreview(deepLinkBlogId)
  }, [subTabValue])

  const reviewStatusOptions = useMemo(
    () => [
      { label: 'Pending + Rework + Published', value: 'PENDING_REVIEW,REWORK_REQUIRED,PUBLISHED' },
      { label: 'Pending Review', value: 'PENDING_REVIEW' },
      { label: 'Rework Required', value: 'REWORK_REQUIRED' },
      { label: 'Published', value: 'PUBLISHED' },
    ],
    []
  )

  const metricsConfig = [
    {
      title: 'Total Blogs',
      value: metrics?.totalBlogs ?? 0,
      icon: <Article sx={{ fontSize: 24 }} />,
    },
    {
      title: 'Pending Review',
      value: metrics?.pendingReview ?? 0,
      icon: <HourglassEmpty sx={{ fontSize: 24 }} />,
    },
    {
      title: 'Rework Required',
      value: metrics?.reworkRequired ?? 0,
      icon: <Build sx={{ fontSize: 24 }} />,
    },
    {
      title: 'Team GTL',
      value: metrics?.teamBlogs ?? 0,
      icon: <Group sx={{ fontSize: 24 }} />,
    },
    {
      title: 'Community Blogs',
      value: metrics?.communityBlogs ?? 0,
      icon: <Forum sx={{ fontSize: 24 }} />,
    },
    {
      title: 'Published',
      value: metrics?.publishedBlogs ?? 0,
      icon: <CheckCircle sx={{ fontSize: 24 }} />,
    },
    {
      title: 'Draft Blogs',
      value: metrics?.draftBlogs ?? 0,
      icon: <EditNote sx={{ fontSize: 24 }} />,
    },
    {
      title: 'NSFW Flagged',
      value: metrics?.nsfwFlagged ?? 0,
      icon: <WarningAmber sx={{ fontSize: 24 }} />,
    },
  ]

  const handleCreateClick = () => {
    if (isAuthenticated) {
      navigate('/blogs/write')
      return
    }
    sessionStorage.setItem('redirectPath', '/blogs/write')
    navigate('/login')
  }

  const handleEditClick = (blog) => {
    setSelectedBlog(blog)
    setOpenDialog(true)
  }

  const handleDeleteClick = (blog) => {
    setConfirmDialog({
      open: true,
      title: 'Confirm Delete',
      message: 'Are you sure you want to delete this team blog?',
      onConfirm: async () => {
        try {
          await deleteBlog(blog.id)
          showNotification('Blog deleted successfully', 'success')
        } catch (error) {
          showNotification(error.message || 'Delete failed', 'error')
        } finally {
          setConfirmDialog((prev) => ({ ...prev, open: false }))
        }
      },
    })
  }

  const handleSubmit = async (blogData) => {
    try {
      if (selectedBlog) {
        await updateBlog(selectedBlog.id, blogData)
        showNotification('Team blog updated successfully', 'success')
      } else {
        await createBlog(blogData)
        showNotification('Team blog created successfully', 'success')
      }
      setOpenDialog(false)
    } catch (error) {
      showNotification(error.message || 'Operation failed', 'error')
      throw error
    }
  }

  const handleOpenReviewDialog = (blogId, action) => {
    setReviewDialog({
      open: true,
      blogId,
      action,
      notes: '',
    })
    setReviewDialogError('')
  }

  const handleCloseReviewDialog = () => {
    setReviewDialog((prev) => ({ ...prev, open: false }))
    setReviewDialogError('')
  }

  const handleReviewAction = async () => {
    const action = reviewDialog.action
    const notes = reviewDialog.notes.trim()
    if (action === 'REWORK' && !notes) {
      setReviewDialogError('Reason for rework is required.')
      return
    }

    try {
      await reviewBlog(reviewDialog.blogId, action, notes)
      showNotification(
        action === 'APPROVE'
          ? 'Blog approved and published'
          : 'Blog sent for rework',
        'success'
      )
      handleCloseReviewDialog()
    } catch (error) {
      const backendMessage = error?.response?.data?.error || error?.response?.data?.message
      showNotification(backendMessage || error.message || 'Review action failed', 'error')
    }
  }

  return (
    <Box>
      <Tabs
        value={subTabValue}
        onChange={handleSubTabChange}
        variant={isSmallScreen ? 'scrollable' : 'standard'}
        scrollButtons={isSmallScreen ? 'auto' : false}
        allowScrollButtonsMobile
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: 3,
          '& .MuiTabs-scroller': {
            overflowX: isSmallScreen ? 'auto !important' : 'hidden',
          },
          '& .MuiTab-root': {
            textTransform: 'none',
            fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
            fontWeight: 500,
            minWidth: { xs: 130, sm: 170, md: 0 },
            minHeight: { xs: 48, sm: 48, md: 48 },
            px: { xs: 1.5, sm: 2, md: 2.5 },
            whiteSpace: 'nowrap',
            flexShrink: 0,
          },
          '& .MuiTab-root .MuiSvgIcon-root': {
            fontSize: { xs: '1.15rem', sm: '1.3rem', md: '1.2rem' },
          },
          '& .MuiTab-iconWrapper': {
            marginBottom: { xs: '6px !important', sm: '0 !important', md: '0 !important' },
          },
          '& .MuiTab-root.Mui-selected': { color: themeColors.primary },
          '& .MuiTabs-indicator': { backgroundColor: themeColors.primary },
          '& .MuiTabs-scrollButtons': {
            color: '#232536',
            '&.Mui-disabled': {
              opacity: 0.3,
            },
          },
        }}
      >
        <Tab
          icon={<Article />}
          iconPosition={isMobileScreen ? 'top' : 'start'}
          label='Team GTL Blogs'
        />
        <Tab
          icon={<Group />}
          iconPosition={isMobileScreen ? 'top' : 'start'}
          label='Community Review Activity'
        />
        <Tab
          icon={<Leaderboard />}
          iconPosition={isMobileScreen ? 'top' : 'start'}
          label='Blog Metrics'
        />
      </Tabs>

      {subTabValue === 0 && (
        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant='h5' sx={{ fontWeight: 600 }}>
              Team GTL Blog Management
            </Typography>
            <Button
              variant='contained'
              startIcon={<Add />}
              onClick={handleCreateClick}
              sx={{
                bgcolor: themeColors.primary,
                color: 'white',
                '&:hover': { bgcolor: themeColors.hover },
                px: 3,
                py: 1,
              }}
            >
              Create Blog
            </Button>
          </Box>

          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            onSearch={handleSearch}
            placeholder='Search team blogs by title, author, or category...'
            sx={{ mb: 3, minWidth: { xs: '100%', md: 560 } }}
          />

          <BlogsTable
            blogs={blogs}
            loading={loading}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
          />

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(event, value) => handlePageChange(value)}
                color='primary'
              />
            </Box>
          )}
        </Box>
      )}

      {subTabValue === 1 && (
        <Box>
          <Typography variant='h5' sx={{ fontWeight: 600, mb: 2 }}>
            Community Blog Review Queue
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <SearchBar
              value={reviewSearchTerm}
              onChange={setReviewSearchTerm}
              onSearch={handleReviewSearch}
              placeholder='Search community blogs, email, title...'
              sx={{ minWidth: { xs: '100%', md: 520 } }}
            />
            <FormControl sx={{ minWidth: 250 }}>
              <InputLabel id='review-status-filter'>Status Filter</InputLabel>
              <Select
                labelId='review-status-filter'
                value={reviewStatusFilter}
                label='Status Filter'
                onChange={(event) =>
                  handleReviewStatusFilter(event.target.value)
                }
              >
                {reviewStatusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <TableContainer
            component={Paper}
            sx={{ boxShadow: 1, borderRadius: 2 }}
          >
            <Table>
              <TableHead sx={{ bgcolor: '#f8fafc' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Writer Email</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>NSFW</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Reviewed By</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Updated</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reviewLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} align='center'>
                      <Typography variant='body2' sx={{ py: 3 }}>
                        Loading review queue...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : reviewBlogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align='center'>
                      <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{ py: 3 }}
                      >
                        No community blogs found for the selected filter
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  reviewBlogs.map((blog) => (
                    <TableRow key={blog.id} hover>
                      <TableCell>{blog.title}</TableCell>
                      <TableCell>{blog.writerEmail || '-'}</TableCell>
                      <TableCell>{blog.reviewStatus || '-'}</TableCell>
                      <TableCell>
                        {blog.nsfwTagged ? (
                          <Chip size='small' color='error' label='Flagged' />
                        ) : (
                          <Chip size='small' color='success' label='Clear' />
                        )}
                      </TableCell>
                      <TableCell>{blog.reviewedByEmail || '-'}</TableCell>
                      <TableCell>{formatTimestamp(blog.updatedDate)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title='Preview Blog'>
                            <IconButton
                              size='small'
                              onClick={() => handleOpenPreview(blog.id)}
                              sx={{ color: themeColors.primary }}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title='Approve & Publish'>
                            <IconButton
                              size='small'
                              onClick={() =>
                                handleOpenReviewDialog(blog.id, 'APPROVE')
                              }
                              sx={{ color: '#16a34a' }}
                            >
                              <CheckCircle />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title='Send for Rework'>
                            <IconButton
                              size='small'
                              onClick={() =>
                                handleOpenReviewDialog(blog.id, 'REWORK')
                              }
                              sx={{ color: '#f59e0b' }}
                            >
                              <ReplayCircleFilled />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {reviewTotalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={reviewTotalPages}
                page={reviewPage}
                onChange={(event, value) => handleReviewPageChange(value)}
                color='primary'
              />
            </Box>
          )}
        </Box>
      )}

      {subTabValue === 2 && (
        <Box>
          <Typography variant='h5' sx={{ fontWeight: 600, mb: 2 }}>
            Blog Workflow Metrics
          </Typography>
          {metricsLoading ? (
            <Typography variant='body2'>Loading metrics...</Typography>
          ) : (
            <Grid container spacing={2}>
              {metricsConfig.map((item) => (
                <Grid key={item.title} item xs={12} sm={6} md={3}>
                  <MetricCard>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 16,
                        left: 16,
                        color: '#ffcf51',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Box
                      sx={{
                        height: '100%',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                      }}
                    >
                      <Typography
                        variant='h4'
                        sx={{ fontWeight: 700, color: '#ffcf51', mb: 1 }}
                      >
                        {item.value}
                      </Typography>
                      <Typography variant='body2'>{item.title}</Typography>
                    </Box>
                  </MetricCard>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      <BlogDialog
        open={openDialog}
        blog={selectedBlog}
        userName={user?.name}
        onClose={() => setOpenDialog(false)}
        onSubmit={handleSubmit}
      />

      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onClose={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
        onConfirm={confirmDialog.onConfirm}
      />

      {previewDialog.loading ? null : (
        <PreviewBlogModal
          open={
            previewDialog.open &&
            !previewDialog.loading &&
            !previewDialog.error &&
            !!previewDialog.blog
          }
          onClose={handleClosePreview}
          title={previewDialog.blog?.title}
          category={previewDialog.blog?.category}
          author={previewDialog.blog?.writerEmail}
          content={previewDialog.blog?.content}
          showSnackbar={showNotification}
          slug={`/blogs/${previewDialog.blog?.slug || ''}`}
        />
      )}

      {/* Loading / error states for preview */}
      <Dialog
        open={
          previewDialog.open && (previewDialog.loading || !!previewDialog.error)
        }
        onClose={handleClosePreview}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle
          sx={{
            color: themeColors.primary,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box component='span'>Blog Preview</Box>
          {isMobileScreen ? (
            <IconButton
              onClick={handleClosePreview}
              sx={{
                backgroundColor: 'white',
                border: '1px solid #232536',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
                boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                flexShrink: 0,
              }}
              aria-label='close blog preview'
              size='small'
            >
              <CloseIcon sx={{ color: '#232536' }} fontSize='small' />
            </IconButton>
          ) : (
            <Box
              sx={{
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                backgroundColor: 'white',
                border: '1px solid #232536',
                borderRadius: '28px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
                padding: '4px 12px',
                flexShrink: 0,
              }}
              onClick={handleClosePreview}
              role='button'
              aria-label='close blog preview'
            >
              <CloseIcon sx={{ color: themeColors.primary }} fontSize='small' />
              <Typography
                variant='button'
                sx={{
                  color: themeColors.primary,
                  marginLeft: 0.5,
                  fontSize: '0.80rem',
                  fontWeight: 'bold',
                }}
              >
                Close
              </Typography>
            </Box>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {previewDialog.loading ? (
            <Typography variant='body2' color='text.secondary'>
              Loading blog content...
            </Typography>
          ) : (
            <Typography variant='body2' color='error'>
              {previewDialog.error}
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={reviewDialog.open}
        onClose={handleCloseReviewDialog}
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle sx={{ color: themeColors.primary, fontWeight: 700 }}>
          {reviewDialog.action === 'APPROVE'
            ? 'Approve and Publish Blog'
            : 'Send Blog for Rework'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin='dense'
            label={
              reviewDialog.action === 'APPROVE'
                ? 'Approval Notes (optional)'
                : 'Rework Reason *'
            }
            fullWidth
            multiline
            minRows={3}
            value={reviewDialog.notes}
            onChange={(event) => {
              setReviewDialog((prev) => ({
                ...prev,
                notes: event.target.value,
              }))
              if (reviewDialogError) {
                setReviewDialogError('')
              }
            }}
            helperText={
              reviewDialog.action === 'APPROVE'
                ? 'Optional notes that will be stored in review activity and sent to the writer.'
                : 'Mandatory reason shown to the writer for required changes.'
            }
            sx={{
              mt: 1,
              '& .MuiInputLabel-root.Mui-focused': {
                color: themeColors.primary,
              },
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: themeColors.primary,
                },
              },
            }}
          />
          {reviewDialogError && (
            <Typography variant='caption' color='error'>
              {reviewDialogError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseReviewDialog}
            sx={{ color: themeColors.primary, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleReviewAction}
            variant='contained'
            sx={{
              textTransform: 'none',
              backgroundColor: themeColors.primary,
              color: '#ffffff', // Ensures font color is white
              '&:hover': {
                backgroundColor: themeColors.hover,
                // color: '#ffffff', // Optional: keep white on hover if variant='contained' doesn't auto-handle it
              },
            }}
          >
            {reviewDialog.action === 'APPROVE'
              ? 'Approve & Publish'
              : 'Send for Rework'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default BlogsTab
